<!--
// Copyright © 2025 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->
<script lang="ts">
  import { type IntlString } from '@hcengineering/platform'
  import {
    CUSTOM_THEME_ID,
    customThemeColorStore,
    getCustomThemePreview,
    getThemeCustomColors,
    normalizeCustomThemeColors,
    resetCurrentCustomThemeColors,
    setCurrentCustomThemeColors,
    themeOptions as availableThemeOptions
  } from '@hcengineering/theme'
  import type { CustomThemeColors, ThemeOption } from '@hcengineering/theme'
  import { createEventDispatcher, getContext } from 'svelte'
  import { type Readable } from 'svelte/store'

  import type { PopupOptions } from '../../types'
  import ThemeButton from './ThemeButton.svelte'
  import ui, { ButtonIcon, IconClose, Label, deviceOptionsStore as deviceInfo } from '../..'

  export let popupOptions: PopupOptions | undefined = undefined

  const dispatch = createEventDispatcher()

  const { currentTheme, setTheme } = getContext<{ currentTheme: Readable<string>, setTheme: (theme: string) => void }>(
    'theme'
  )

  const themeLabels = new Map<string, IntlString>([
    ['theme-light', ui.string.ThemeLight],
    ['theme-dark', ui.string.ThemeDark],
    ['theme-system', ui.string.ThemeSystem]
  ])

  const customColorControls: Array<{ key: keyof CustomThemeColors, label: string }> = [
    { key: 'sidebar', label: 'Sidebar' },
    { key: 'background', label: 'Background' },
    { key: 'surface', label: 'Surface' },
    { key: 'accent', label: 'Accent' },
    { key: 'onAccentText', label: 'Accent text' },
    { key: 'text', label: 'Text' },
    { key: 'mutedText', label: 'Muted text' }
  ]

  const hexColorPattern = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i

  let dimBackground = true
  let themes: Array<ThemeOption & { intlLabel: IntlString | undefined }> = []
  let editorTheme = ''
  let editorColors: CustomThemeColors = getThemeCustomColors(CUSTOM_THEME_ID)

  $: isCustomColorEditor = editorTheme === CUSTOM_THEME_ID

  $: themes = availableThemeOptions.map((theme) => ({
    ...theme,
    preview: theme.id === CUSTOM_THEME_ID ? getCustomThemePreview($customThemeColorStore) : theme.preview,
    intlLabel: themeLabels.get(theme.id)
  }))

  $: if (popupOptions !== undefined && popupOptions.showOverlay !== dimBackground) {
    popupOptions = { ...popupOptions, showOverlay: dimBackground }
  }

  $: if ($currentTheme !== editorTheme) {
    editorTheme = $currentTheme
    editorColors = getThemeCustomColors($currentTheme)
  }

  $: if ($currentTheme === CUSTOM_THEME_ID && editorTheme === CUSTOM_THEME_ID) {
    editorColors = $customThemeColorStore
  }

  function selectTheme (theme: string): void {
    if ($currentTheme === theme) return
    editorTheme = theme
    editorColors = getThemeCustomColors(theme)
    setTheme(theme)
  }

  function updateCustomColor (key: keyof CustomThemeColors, value: string): void {
    const color = value.trim()
    if (!hexColorPattern.test(color)) return

    const colors = normalizeCustomThemeColors({
      ...editorColors,
      [key]: color
    })
    editorTheme = CUSTOM_THEME_ID
    editorColors = colors
    setCurrentCustomThemeColors(colors)

    if ($currentTheme !== CUSTOM_THEME_ID) {
      setTheme(CUSTOM_THEME_ID)
    }
  }

  function updateCustomColorFromEvent (key: keyof CustomThemeColors, event: Event): void {
    updateCustomColor(key, (event.currentTarget as HTMLInputElement).value)
  }

  function resetCustomTheme (): void {
    const colors = resetCurrentCustomThemeColors()
    editorTheme = CUSTOM_THEME_ID
    editorColors = colors

    if ($currentTheme !== CUSTOM_THEME_ID) {
      setTheme(CUSTOM_THEME_ID)
    }
  }

  function updateDimBackground (event: Event): void {
    dimBackground = (event.currentTarget as HTMLInputElement).checked
  }

  $: $deviceInfo.theme = $currentTheme
</script>

<div class="antiPopup theme-selector-popup">
  <div class="theme-selector-header" data-popup-drag-handle="true">
    <span class="theme-selector-title">Themes</span>
    <div class="theme-selector-actions" data-popup-drag-disabled="true">
      <label class="theme-dim-toggle" on:mousedown|stopPropagation>
        <input type="checkbox" checked={dimBackground} on:change={updateDimBackground} />
        <span>Dim background</span>
      </label>
      <ButtonIcon icon={IconClose} kind={'tertiary'} size={'small'} on:click={() => dispatch('close')} />
    </div>
  </div>

  <div class="theme-selector-content">
    <div class="theme-selector-grid" data-popup-drag-disabled="true">
      {#each themes as theme}
        {@const selected = $currentTheme === theme.id}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          class="statusPopup-option theme-selector-option"
          class:selected
          on:click={() => {
            selectTheme(theme.id)
          }}
        >
          <ThemeButton theme={theme.id} focused={$currentTheme} mode={theme.mode} preview={theme.preview} />
          <span class="label overflow-label">
            {#if theme.intlLabel !== undefined}
              <Label label={theme.intlLabel} />
            {:else}
              {theme.label}
            {/if}
          </span>
        </div>
      {/each}
    </div>

    <div class="custom-theme-editor" data-popup-drag-disabled="true">
      <div class="custom-theme-editor-header">
        <span class="custom-theme-editor-title">{isCustomColorEditor ? 'Custom colors' : 'Theme colors'}</span>
        {#if isCustomColorEditor}
          <button class="custom-theme-reset" type="button" on:click={resetCustomTheme}>Reset</button>
        {/if}
      </div>
      <div class="custom-theme-controls">
        {#each customColorControls as control}
          {@const labelId = `custom-color-${control.key}-label`}
          <div class="custom-color-control" role="group" aria-labelledby={labelId}>
            <span id={labelId} class="custom-color-label">{control.label}</span>
            <span class="custom-color-inputs">
              <input
                class="custom-color-swatch"
                type="color"
                aria-label={`${control.label} swatch`}
                value={editorColors[control.key]}
                on:input={(event) => updateCustomColorFromEvent(control.key, event)}
              />
              <input
                class="custom-color-value"
                type="text"
                spellcheck="false"
                aria-label={`${control.label} hex value`}
                value={editorColors[control.key]}
                on:input={(event) => updateCustomColorFromEvent(control.key, event)}
              />
            </span>
          </div>
        {/each}
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .antiPopup.theme-selector-popup {
    width: min(44rem, calc(100vw - 2rem));
    max-width: min(44rem, calc(100vw - 2rem));
    max-height: min(42rem, calc(100vh - 2rem));
  }

  .theme-selector-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    padding: 0.625rem 0.75rem 0.625rem 1rem;
    min-width: 0;
    border-bottom: 1px solid var(--theme-popup-divider);
    cursor: move;
  }

  .theme-selector-title {
    overflow: hidden;
    min-width: 0;
    text-transform: uppercase;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--theme-caption-color);
  }

  .theme-selector-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
    cursor: default;
  }

  .theme-dim-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
    color: var(--global-secondary-TextColor);
    font-size: 0.75rem;
    white-space: nowrap;
    cursor: pointer;

    input {
      margin: 0;
      accent-color: var(--selector-active-BackgroundColor);
      cursor: pointer;
    }
  }

  .theme-selector-content {
    overflow-x: hidden;
    overflow-y: auto;
    padding: 1rem;
    min-width: 0;
    min-height: 0;
    scrollbar-width: thin;
  }

  .custom-theme-editor {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--theme-popup-divider);
  }

  .custom-theme-editor-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-width: 0;
  }

  .custom-theme-editor-title {
    overflow: hidden;
    min-width: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--theme-caption-color);
  }

  .custom-theme-reset {
    flex-shrink: 0;
    padding: 0.25rem 0.5rem;
    min-width: 0;
    height: 1.75rem;
    color: var(--global-primary-TextColor);
    background: var(--button-tertiary-hover-BackgroundColor);
    border: 1px solid var(--button-secondary-BorderColor);
    border-radius: 0.375rem;
    cursor: pointer;

    &:hover {
      background: var(--button-tertiary-active-BackgroundColor);
    }
  }

  .custom-theme-controls {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
    gap: 0.75rem;
  }

  .custom-color-control {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    min-width: 0;
  }

  .custom-color-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.75rem;
    color: var(--global-tertiary-TextColor);
  }

  .custom-color-inputs {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  .custom-color-swatch {
    flex: 0 0 2rem;
    padding: 0;
    width: 2rem;
    height: 2rem;
    background: transparent;
    border: 1px solid var(--input-BorderColor);
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .custom-color-value {
    flex: 1 1 auto;
    min-width: 0;
    height: 2rem;
    padding: 0 0.5rem;
    color: var(--global-primary-TextColor);
    background: var(--input-BackgroundColor);
    border: 1px solid var(--input-BorderColor);
    border-radius: 0.375rem;
    font: inherit;
    text-transform: uppercase;
  }

  .theme-selector-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(7.5rem, 1fr));
    gap: 1rem 0.75rem;
  }

  .theme-selector-option {
    justify-content: flex-start;
    margin-right: 0;
    padding: 0.5rem 0.375rem;
    border-radius: 0.5rem;

    &:not(.selected):hover {
      background-color: var(--theme-popup-hover);
    }

    :global(.statusPopupThemeButton) {
      width: 5.75rem;
      height: 4rem;
    }

    :global(.statusPopupThemeButton .palette-sidebar) {
      width: 1.625rem;
    }

    :global(.statusPopupThemeButton .palette-paper) {
      width: 4rem;
      height: 2.625rem;
    }

    :global(.statusPopupThemeButton:not(.both) .light-container .paper),
    :global(.statusPopupThemeButton:not(.both) .dark-container .paper) {
      margin: 1.125rem 0 0 1.25rem;
    }

    :global(.statusPopupThemeButton.both .light-container .paper),
    :global(.statusPopupThemeButton.both .dark-container .paper) {
      margin: 1.125rem 0 0 0.625rem;
    }

    .label {
      display: -webkit-box;
      overflow: hidden;
      max-width: 7rem;
      min-height: 2.1rem;
      text-align: center;
      white-space: normal;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }

  @media screen and (max-width: 480px) {
    .antiPopup.theme-selector-popup {
      width: calc(100vw - 1rem);
      max-width: calc(100vw - 1rem);
      max-height: calc(100vh - 1rem);
    }

    .theme-selector-grid {
      grid-template-columns: repeat(auto-fill, minmax(6.75rem, 1fr));
    }
  }
</style>
